import { WizardStep } from "@/Components/wizard/wizardStep";
import { MutableRefObject } from "react";

interface TeamBuilderContextType {
    dialogOpen: boolean;
    setDialogOpen: (open: boolean) => void;
    demoMode: boolean;
    toggleDemoMode: (enable: boolean) => void;
    showUserFilters: boolean;
    setShowUserFilters: (show: boolean) => void;
    showTeamFilters: boolean;
    setShowTeamFilters: (show: boolean) => void;
    userSelection: {};
    setUserSelection: React.Dispatch<React.SetStateAction<{}>>;
    teamMemberSelection: {};
    setTeamMemberSelection: React.Dispatch<React.SetStateAction<{}>>;
    HandleAddUserToTeam: (demoUserIds?: string[]) => void;
    HandleGroupRemoveClicked: () => void;
    setTourStateInitial: () => void;
    setTourStateWithUserAdded: () => void;
}

interface BasicPageTourProps {
    teamBuilderContext?: MutableRefObject<TeamBuilderContextType>;
}

export function getBasicPageTourSteps({ teamBuilderContext }: BasicPageTourProps = {}): WizardStep[] {

    const steps: WizardStep[] = [
        {
            stepId: 1,
            stepName: "Users Table",
            stepDescription: "This is the Users table. This contains all available users that can be added to your team.",
            isCompleted: false,
            stepComponentId: "users-table",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 2,
            stepName: "Team Members Table",
            stepDescription: "This is the Team Members table. This contains all users that are currently part of your team.",
            isCompleted: false,
            stepComponentId: "team-members-table",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        // {
        //     stepId: 12,
        //     stepName: "Column Filters",
        //     stepDescription: "You can also filter the data in the columns",
        //     isCompleted: false,
        //     stepComponentId: "columnFilter",
        //     useClassSelector: true,
        //     classSelectorNumber: 1,
        //     displaySide: "right" as "right" | "left" | "top" | "bottom",
        // },
        {
            stepId: 3,
            stepName: "Select All Rows",
            stepDescription: "Use this checkbox to select all rows on the current page",
            isCompleted: false,
            stepComponentId: "table-select-all",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 4,
            stepName: "Select Row",
            stepDescription: "Use the rows checkboxes to select specific users",
            isCompleted: false,
            stepComponentId: "table-row-checkbox",
            useClassSelector: true,
            classSelectorNumber: 0,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
            functionCall: () => {
                if (teamBuilderContext?.current) {
                    // in case of stepping back, ensure a row is selected to show the add button
                    teamBuilderContext.current.setTourStateInitial();
                    // Wait for state to update before setting selection
                    setTimeout(() => {
                        teamBuilderContext.current?.setUserSelection({ ["demo-user-1"]: true });
                    }, 0);
                }
            },
        },
        {
            stepId: 5,
            stepName: "Add Team Member Button",
            stepDescription: "Use this button to add the selected user from the Users table to your team.",
            isCompleted: false,
            stepComponentId: "add-team-member-button",
            displaySide: "top" as "right" | "left" | "top" | "bottom",
            functionCall: () => {
                setTimeout(() => {
                    teamBuilderContext?.current?.setTourStateWithUserAdded?.();
                }, 500);
            },
        },
        {
            stepId: 6,
            stepName: "Row Actions",
            stepDescription: "This menu allows you to change the user's role or remove them from the team.",
            isCompleted: false,
            stepComponentId: "table-row-menu-button",
            useClassSelector: true,
            classSelectorNumber: 5,
            displaySide: "left" as "right" | "left" | "top" | "bottom",
            sendClick: true,
        },
        {
            stepId: 7,
            stepName: "Select a team member",
            stepDescription: "Use the checkboxes to select multiple team members, this will reveal the group actions button",
            isCompleted: false,
            stepComponentId: "team-table-row-checkbox",
            useClassSelector: true,
            classSelectorNumber: 5,
            displaySide: "left" as "right" | "left" | "top" | "bottom",
            showAboveDialog: true,
            functionCall: () => {
                if (teamBuilderContext?.current) {
                    teamBuilderContext.current.setTeamMemberSelection({ ["demo-user-1"]: true });
                }
            },
        },
        {
            stepId: 8,
            stepName: "Group Actions",
            stepDescription: "Group actions offer a quicker way to manage multiple rows. With multiple rows selected you can perform group actions such as removing from the team and group setting role",
            isCompleted: false,
            stepComponentId: "table-group-actions",
            displaySide: "top" as "right" | "left" | "top" | "bottom",
            functionCall: () => {
                if (teamBuilderContext?.current) {
                    // incase of stepping back, ensure a row is selected to show the group actions menu
                    teamBuilderContext.current.setTourStateWithUserAdded();
                    // Wait for state to update before setting selection
                    setTimeout(() => {
                        teamBuilderContext.current?.setTeamMemberSelection({ ["demo-user-1"]: true });
                    }, 0);
                }
            },
            sendClick: true,
        },
        {
            stepId: 9,
            stepName: "Remove Team Member Button",
            stepDescription: "Use this button to remove the selected members from the Team Members table. These members will then be available again in the Users table.",
            isCompleted: false,
            stepComponentId: "remove-team-member-button",
            displaySide: "top" as "right" | "left" | "top" | "bottom",
            functionCall: () => {
                // in case of stepping back, ensure a row is selected to show the remove button
                teamBuilderContext?.current?.setTourStateWithUserAdded?.();
                teamBuilderContext?.current?.setShowUserFilters?.(false);
                setTimeout(() => {
                    teamBuilderContext?.current?.setTourStateInitial?.();
                }, 500);
            }
        },
        {
            stepId: 10,
            stepName: "Filter Options",
            stepDescription: "If your user list is extensive and you need to filter it, clicking this icon will show filter options for the Users table",
            isCompleted: false,
            stepComponentId: "show-user-filters",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
            functionCall: () => {
                setTimeout(() => {
                    teamBuilderContext?.current?.setShowUserFilters?.(true);
                }, 500);
            },
        },
        {
            stepId: 11,
            stepName: "View Options",
            stepDescription: "Select the columns you want to view",
            isCompleted: false,
            stepComponentId: "viewOptionsButton",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 12,
            stepName: "Global Search",
            stepDescription: "Use the global search to find specific data",
            isCompleted: false,
            stepComponentId: "globalSearch",
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 13,
            stepName: "Reset Filters",
            stepDescription: "Reset all filters to view all data",
            isCompleted: false,
            stepComponentId: "resetFilters",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 14,
            stepName: "Export Data",
            stepDescription: "Export the current data view to a CSV file",
            isCompleted: false,
            stepComponentId: "exportButton",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 15,
            stepName: "Column Dragging",
            stepDescription: "You can drag and drop the columns to reorder them by clicking and holding the grip icon",
            isCompleted: false,
            stepComponentId: "columnDraggable",
            useClassSelector: true,
            classSelectorNumber: 1,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 16,
            stepName: "Column Sorting",
            stepDescription: "Choose how to sort the columns by clicking the column header",
            isCompleted: false,
            stepComponentId: "columnSortable",
            useClassSelector: true,
            classSelectorNumber: 1,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 17,
            stepName: "Column Filters",
            stepDescription: "You can also filter the data in the columns",
            isCompleted: false,
            stepComponentId: "columnFilter",
            useClassSelector: true,
            classSelectorNumber: 1,
            displaySide: "right" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 18,
            stepName: "Rows Per Page",
            stepDescription: "Choose how many rows to display per page",
            isCompleted: false,
            stepComponentId: "rowsPerPage",
            functionCall: () => { if (teamBuilderContext?.current) { teamBuilderContext.current.setDialogOpen(false); } },
            displaySide: "top" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 19,
            stepName: "Page Number",
            stepDescription: "This shows the current page number and total pages",
            isCompleted: false,
            stepComponentId: "pageIndicator",
            displaySide: "top" as "right" | "left" | "top" | "bottom",
        },
        {
            stepId: 20,
            stepName: "Pagination Controls",
            stepDescription: "Navigate through pages of data",
            isCompleted: false,
            stepComponentId: "pagination-controls",
            displaySide: "left" as "right" | "left" | "top" | "bottom",
        },
    ];

    return steps;
}

export function getBasicTourSteps({ teamBuilderContext }: BasicPageTourProps = {}): WizardStep[] {
    return getBasicPageTourSteps({ teamBuilderContext });
}